import { Box, Flex } from 'components';
import { ImagesPicker } from 'components/ImagesPicker/ImagesPicker';
import { P } from 'components/Typography/Typography';
import { UserSelect } from 'components/_form/UserSelect';
import {
  Button,
  LabelPosition,
  SwitchButton,
  TextareaField,
  TextField
} from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AreaAccordionItem } from 'types/area';
import { IAreaFormFields } from 'types/forms/area';
import { getFile } from 'utilities/files';
import { useEditAreaMutation } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { companySupportedLanguageCodesExceptDefault } from 'utilities/companyLanguageCodes';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { mapUsersToSelectOptions } from 'utilities/userSelectOption';
import { toast } from 'react-toastify';

interface Props {
  data: AreaAccordionItem;
  onCancel: () => void;
}

export const AreaDetailsForm: React.FC<Props> = ({ data, onCancel }) => {
  const { register, control, reset, watch, setValue, handleSubmit } =
    useForm<IAreaFormFields & { images?: File[]; state?: string }>();

  const { mutateAsync: editArea, isPending } = useEditAreaMutation(String(data.id));
  const { data: users = [] } = useUsersQuery();
  const { data: company } = useMainCompanyQuery();

  const translationLocales = useMemo(
    () => companySupportedLanguageCodesExceptDefault(company),
    [company?.default_language_code, (company?.available_language_codes ?? []).join(',')]
  );

  const userOptions = useMemo(
    () => mapUsersToSelectOptions(users),
    [users]
  );

  const loadData = useCallback(async () => {
    const images = data.image_urls?.length
      ? await Promise.all(data.image_urls.map(getFile))
      : [];

    const name_translations: Record<string, string> = {};
    translationLocales.forEach((loc) => {
      name_translations[loc] = data.name_translations?.[loc] ?? '';
    });

    reset({
      id_code: data.id_code,
      name: data.name,
      description: data.description,
      state: data.state,
      owner_id: data.owner ? String(data.owner.id) : undefined,
      images,
      name_translations
    });
  }, [
    data.id,
    data.id_code,
    data.name,
    data.description,
    data.state,
    data.image_urls,
    data.owner?.id,
    JSON.stringify(data.name_translations ?? {}),
    translationLocales.join(','),
    reset
  ]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const onSubmit = async (fields: IAreaFormFields & { images?: File[] }) => {
    const { images, ...rest } = fields;
    const name_translations: Record<string, string> = {};
    translationLocales.forEach((loc) => {
      name_translations[loc] = (rest.name_translations?.[loc] ?? '').trim();
    });
    try {
      await editArea({
        area: { ...rest, name_translations, images: images ?? [] }
      });
      toast.success(t('common.success'));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('common.anErrorOccurred');
      toast.error(message);
    }
  };

  return (
    <Flex flexWrap="wrap">
      <Box width={['100%', '50%']}>
        <ImagesPicker
          images={watch('images') || []}
          setImages={(images: File[]) => setValue('images', images)}
          allowEdit
        />
      </Box>

      <Flex width={['100%', '50%']} flexDirection="column" pl={4}>
        <Flex width="100%" justifyContent="space-between" mb={2}>
          <Flex flexDirection="column">
            <P mb={1} variant="body">
              {t('administration.active?')}
            </P>
            <Controller
              control={control}
              name="state"
              render={({ field: { value, onChange } }) => (
                <SwitchButton
                  value={value === 'active'}
                  onChange={(checked) => onChange(checked ? 'active' : 'inactive')}
                />
              )}
            />
          </Flex>

          <Box>
            <Button variant="red">{t('administration.deleteArea')}</Button>
          </Box>
        </Flex>

        <TextField {...register('id_code')} label="administration.areaCode" />
        <TextField {...register('name')} label="administration.name" />
        <Flex flexWrap="wrap" gap="12px" mt={2} width="100%">
          {translationLocales.map((locale) => (
            <Box key={locale} width={['100%', 'calc(50% - 6px)']}>
              <TextField
                {...register(`name_translations.${locale}` as `name_translations.${string}`)}
                label={`${locale.trim().toUpperCase()}:`}
                labelAsPlainText
                labelPosition={LabelPosition.INLINE}
              />
            </Box>
          ))}
        </Flex>
        <Box mt={2}>
          <TextareaField
            {...register('description')}
            label={t('administration.description') as string}
            rows={10}
          />
        </Box>

        <Box mt={2}>
          <Controller
            control={control}
            name="owner_id"
            render={({ field: { value, onChange } }) => (
              <UserSelect
                label="administration.areaOwner"
                options={userOptions}
                selectedOptions={userOptions.find((o) => o.value === value)}
                onChange={(opt) => onChange((opt as IOption)?.value)}
              />
            )}
          />
        </Box>

        <Flex mt={3} justifyContent="flex-end">
          <Box mr={2}>
            <Button
              variant="greyWhite"
              onClick={() => {
                reset();
                onCancel();
              }}
            >
              {t('buttons.cancel')}
            </Button>
          </Box>
          <Button
            variant="eucalyptus"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending}
          >
            {t('buttons.save')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
